import React from 'react';
import { AbsoluteFill, Img, useCurrentFrame } from 'remotion';
import { Layer, ProjectState } from "../../shared";
import DetectedObjects from './DetectedObjects';

type Props = { 
    layer: Layer,
    project: ProjectState
};

function ImageLayer({ layer, project }: Props) {
    const frame = useCurrentFrame();
    const { 
        blendMode, 
        opacity,
        startFrame,
        endFrame,
        visible,
        rotation,
    } = layer;
    const isVisible = visible && frame >= startFrame && frame <= endFrame;
    const { sliceLength, animation } = project;
    let { x, y } = layer.position;
    let { width, height } = layer.dimensions;
    let clipPath;
    if (sliceLength && isVisible) {
        const clipWidth = width / sliceLength;
        const clipLeft = clipWidth * (endFrame - frame)
        const clipRight = width - clipLeft - clipWidth;
        const clipTop = 0;
        const clipBottom = 0;
        clipPath = `inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px)`;
    }
    let filter = "";
    if (layer.adjustments && isVisible) {
        const {
            brightnessContrast,
            hueSaturation,
            lensBlur,
        } = layer.adjustments;
        if (brightnessContrast) {
            const {
                brightness,
                contrast
            } = brightnessContrast;
            filter = `${filter} brightness(${brightness + 1})`;
            filter = `${filter} contrast(${contrast + 1})`;
        }
        if (hueSaturation) {
            const {
                hue,
                saturation
            } = hueSaturation;
            filter = `${filter} hue-rotate(${hue * 180}deg)`;
            filter = `${filter} saturate(${saturation + 1})`;
        }
        if (lensBlur) {
            const {
                radius
            } = lensBlur;
            filter = `${filter} blur(${radius}px)`;
        }
    }
    let transform = '';
    if (rotation && isVisible) {
        transform = `${transform} rotate(${rotation}deg)`;
    }

    const _x = x || 0;
    const _y = y || 0;
    const _width = width || 0;
    const _height = height || 0;
    const _frame = project.durationInFrames - frame;
    switch (animation) {
        case "zoomIn":
            x = _x - frame;
            width = _width + frame + frame;
            height = width * _height / _width;
            y = _y - frame;
            break;
        case "zoomOut":
            x = _x - _frame;
            width = _width + _frame + _frame;
            height = width * _height / _width;
            y = _y - _frame;
            break;
        case "panDown":
            x = _x - project.durationInFrames;
            width = _width + project.durationInFrames + project.durationInFrames;
            height = width * _height / _width;
            y = _y - frame;
            break;
        case "panUp":
            x = _x - project.durationInFrames;
            width = _width + project.durationInFrames + project.durationInFrames;
            height = width * _height / _width;
            y = _y - _frame;
            break;
        case "panRight":
            y = _y - project.durationInFrames;
            height = _height + project.durationInFrames + project.durationInFrames;
            width = height * _width / _height;
            x = _x - frame;
            break;
        case "panLeft":
            y = _y - project.durationInFrames;
            height = _height + project.durationInFrames + project.durationInFrames;
            width = height * _width / _height;
            x = _x - _frame;
            break;
    }
    return (
        <>
            <AbsoluteFill>
                <DetectedObjects 
                    project={project}
                    layer={layer}>
                    <Img
                        style={{ 
                            position: 'absolute',
                            top: y || 0,
                            left: x || 0,
                            width,
                            height,
                            opacity: isVisible ? opacity : 0,
                            mixBlendMode: blendMode,
                            clipPath,
                            filter,
                            transform,
                        }}
                        src={layer.url} 
                        draggable="false"
                        crossOrigin="anonymous"
                    />
                </DetectedObjects>
            </AbsoluteFill>
        </>
    )
}


export default ImageLayer;
